/**
 * Descriptor de Empresa (pessoa jurídica). Todos os campos de `EmpresaOut`, mais as
 * correlações em abas (o mesmo padrão de Pessoa): contatos, endereços, sócios/funcionários
 * (o outro lado do vínculo pessoa↔empresa), documentos, núcleo econômico, e o dossiê + grafo.
 */
import { Building2 } from 'lucide-react';
import { createElement } from 'react';
import { PainelEnriquecerEmpresa } from '@/components/crud/paineis/PainelEnriquecerEmpresa';
import { PainelFontesPublicas } from '@/components/crud/paineis/PainelFontesPublicas';
import { PainelNucleo } from '@/components/crud/paineis/PainelNucleo';
import type { EmpresaCad } from '@/lib/api';
import { definirRecurso } from '@/lib/recursos/tipos';

export const empresa = definirRecurso<EmpresaCad>({
  chave: 'empresas',
  endpoint: '/cadastro/empresas',
  ownerTipo: 'empresa',
  titulo: 'Empresas',
  tituloSingular: 'empresa',
  descricao: 'Pessoas jurídicas — enriquecidas pela Receita (CNPJ) e seus sócios.',
  icone: Building2,
  capacidades: { podeEditar: true, podeExcluir: true },
  buscaPlaceholder: 'Buscar por razão social, nome fantasia ou CNPJ',
  rotuloItem: (e) => e.nome_fantasia || e.razao_social,

  colunas: [
    { chave: 'razao_social', rotulo: 'Razão social' },
    { chave: 'nome_fantasia', rotulo: 'Nome fantasia', ocultarEm: 'md' },
    { chave: 'cnpj', rotulo: 'CNPJ', formato: 'cnpj', mono: true },
  ],

  camposCriar: [
    { chave: 'razao_social', rotulo: 'Razão social', tipo: 'texto', obrigatorio: true },
    { chave: 'cnpj', rotulo: 'CNPJ', tipo: 'documento', formato: 'cnpj' },
  ],

  // Cadastro por documento: anexa o Cartão CNPJ, contrato social, inscrições…, a IA lê os dados e o
  // operador revisa antes de gravar — ou insere à mão. O tipo é escolhido no drawer.
  importar: {
    rotulo: 'Importar documento',
    selecionavel: true,
    corpoMinimo: { razao_social: 'Cadastro por documento' },
    descricao:
      'Anexe um documento da empresa (Cartão CNPJ, contrato social, inscrições…). A IA lê razão social, CNPJ e demais dados; você revisa antes de gravar.',
  },

  // Ingestão em massa: sobe um Excel/CSV com muitas empresas; a IA sugere o mapa coluna→campo
  // (inclui segmento, contatos, endereço e sócios), o operador revisa, e a importação roda
  // assíncrona com progresso. O layout revisado pode virar um molde reutilizável.
  importarPlanilha: {
    endpoint: '/cadastro/ingestao/empresas',
    descricao:
      'Suba uma planilha (Excel ou CSV) com muitas empresas. A IA sugere para que serve cada coluna — inclusive segmento, contatos, endereço e sócios; você revisa antes de importar.',
  },

  gruposEdicao: [
    {
      id: 'identificacao',
      titulo: 'Identificação',
      campos: [
        { chave: 'razao_social', rotulo: 'Razão social', tipo: 'texto', obrigatorio: true, colSpan: 2 },
        { chave: 'nome_fantasia', rotulo: 'Nome fantasia', tipo: 'texto', colSpan: 2 },
        // Ramo/segmento de negócio (Software House, Imobiliária…) — referência extensível: o operador
        // escolhe da base ampla ou cadastra um novo na hora (como cargos/profissões).
        {
          chave: 'segmento_id',
          rotulo: 'Segmento / Ramo',
          tipo: 'referencia',
          refEndpoint: '/cadastro/segmentos',
          colSpan: 2,
        },
        { chave: 'cnpj', rotulo: 'CNPJ', tipo: 'documento', formato: 'cnpj' },
        { chave: 'data_abertura', rotulo: 'Data de abertura', tipo: 'data' },
        { chave: 'situacao_cadastral', rotulo: 'Situação cadastral', tipo: 'texto' },
      ],
    },
    {
      id: 'fiscal',
      titulo: 'Dados fiscais e societários',
      campos: [
        { chave: 'inscricao_estadual', rotulo: 'Inscrição estadual', tipo: 'texto' },
        { chave: 'inscricao_municipal', rotulo: 'Inscrição municipal', tipo: 'texto' },
        { chave: 'natureza_juridica', rotulo: 'Natureza jurídica', tipo: 'texto' },
        { chave: 'cnae_principal', rotulo: 'CNAE principal', tipo: 'texto' },
        { chave: 'porte', rotulo: 'Porte', tipo: 'texto' },
        { chave: 'regime_tributario', rotulo: 'Regime tributário', tipo: 'texto' },
        { chave: 'capital_social', rotulo: 'Capital social', tipo: 'dinheiro' },
      ],
    },
    {
      id: 'sistema',
      titulo: 'Situação e observações',
      campos: [
        { chave: 'ativo', rotulo: 'Ativo', tipo: 'booleano' },
        { chave: 'observacoes', rotulo: 'Observações', tipo: 'texto-longo', colSpan: 2 },
        { chave: 'created_at', rotulo: 'Criado em', tipo: 'leitura' },
        { chave: 'updated_at', rotulo: 'Atualizado em', tipo: 'leitura' },
      ],
    },
  ],

  paineis: [
    { tipo: 'contatos' },
    { tipo: 'enderecos' },
    {
      tipo: 'vinculos',
      titulo: 'Pessoas vinculadas',
      endpointGet: '/cadastro/empresas/{id}/pessoas',
      endpointPost: '/cadastro/empregos',
    },
    { tipo: 'documentos' },
    { tipo: 'dossie' },
  ],

  abas: [
    {
      id: 'dados',
      rotulo: 'Dados da Empresa',
      grupos: ['identificacao', 'fiscal', 'sistema'],
      paineis: [
        { tipo: 'tags' },
        // Botão de fallback: reenriquecer pela Receita quando o automático falhou (ex.: 429).
        {
          tipo: 'custom',
          id: 'enriquecer-empresa',
          titulo: 'Enriquecimento pela Receita',
          render: ({ item, recarregar }) =>
            createElement(PainelEnriquecerEmpresa, { item, recarregar }),
        },
      ],
    },
    { id: 'contatos', rotulo: 'Contatos', paineis: [{ tipo: 'contatos' }] },
    { id: 'enderecos', rotulo: 'Endereços', paineis: [{ tipo: 'enderecos' }] },
    {
      id: 'socios',
      rotulo: 'Pessoas Vinculadas',
      paineis: [
        {
          tipo: 'vinculos',
          titulo: 'Pessoas vinculadas',
          endpointGet: '/cadastro/empresas/{id}/pessoas',
          endpointPost: '/cadastro/empregos',
        },
      ],
    },
    { id: 'documentos', rotulo: 'Documentos', paineis: [{ tipo: 'documentos' }] },
    {
      id: 'nucleo',
      rotulo: 'Núcleo',
      paineis: [
        {
          tipo: 'custom',
          id: 'nucleo-economico',
          titulo: 'Núcleo econômico',
          render: ({ item }) =>
            createElement(PainelNucleo, { ownerTipo: 'empresa', ownerId: item.id }),
        },
      ],
    },
    // A Receita aparece aqui também, mas CONSULTANDO: devolve o retrato cadastral datado sem
    // alterar a ficha. Quem adota os dados é o botão de enriquecer, na aba de dados.
    {
      id: 'fontes',
      rotulo: 'Fontes Públicas',
      paineis: [
        {
          tipo: 'custom',
          id: 'empresa-fontes-publicas',
          titulo: 'Fontes públicas',
          render: ({ item }) => createElement(PainelFontesPublicas, { item, tipo: 'empresa' }),
        },
      ],
    },
    { id: 'dossie', rotulo: 'Dossiê', paineis: [{ tipo: 'dossie' }] },
    { id: 'grafo', rotulo: 'Grafo', paineis: [{ tipo: 'grafo' }] },
  ],
});
